import { c as _c } from "react/compiler-runtime";
import { ContactDetails } from '@/components/Atoms/ContactDetails/ContactDetails';
import { ModalForm } from '@/components/Atoms/ModalForm/ModalForm';
import { CsrfForm } from '@/components/Molecules/CsrfForm/CsrfForm';
import { Dialog, useDialog } from '@/components/Molecules/Dialog/Dialog';
import type { IAccountDetails } from '@/components/Organisms/AccountPreferencesForm/AccountPreferencesForm';
import { AccountPreferencesForm } from '@/components/Organisms/AccountPreferencesForm/AccountPreferencesForm';
import { useAppContext } from '@/contexts/AppContext/AppContext';
import { useFetchResponseHandler } from '@/hooks/useFetchResponseHandler';
import useTranslation from '@/hooks/useTranslation';
import type { ISignupOptions } from '@/interfaces';
import clientStorage from '@/services/ClientStorage';
import { BrowserHttpClient } from '@/services/Http/BrowserHttpClient';
import Trans from 'next-translate/Trans';
import type { FC, ReactNode } from 'react';
import { useEffect, useState } from 'react';
const POPUP_STORAGE_KEY = ['confirmDetails', 'disableDialog'];
const initialDataFetcher = async () => Promise.all([new BrowserHttpClient().get('/users/account'), new BrowserHttpClient().get('/public/signup_options')]);
export const ConfirmDetailsPopup: FC = () => {
  const $ = _c(5);
  const {
    requireDetailConfirmation
  } = useAppContext();
  const [shouldPrompt, setShouldPrompt] = useState(false);
  let t0;
  let t1;
  if ($[0] !== requireDetailConfirmation) {
    t0 = () => {
      if (requireDetailConfirmation) {
        const disableDialog = clientStorage.get(POPUP_STORAGE_KEY);
        if (disableDialog) {
          return;
        }
        clientStorage.remove(POPUP_STORAGE_KEY);
        setShouldPrompt(true);
      }
    };
    t1 = [requireDetailConfirmation];
    $[0] = requireDetailConfirmation;
    $[1] = t0;
    $[2] = t1;
  } else {
    t0 = $[1];
    t1 = $[2];
  }
  useEffect(t0, t1);
  let t2;
  if ($[3] !== shouldPrompt) {
    t2 = shouldPrompt && <ConfirmDetailsLoader />;
    $[3] = shouldPrompt;
    $[4] = t2;
  } else {
    t2 = $[4];
  }
  return t2;
};
const ConfirmDetailsLoader: FC = () => {
  const $ = _c(3);
  const {
    data
  } = useFetchResponseHandler(initialDataFetcher);
  if (data) {
    let t0;
    if ($[0] !== data[0] || $[1] !== data[1]) {
      t0 = <ConfirmDetailsPopupModal accountDetails={data[0]} formOptions={data[1]} />;
      $[0] = data[0];
      $[1] = data[1];
      $[2] = t0;
    } else {
      t0 = $[2];
    }
    return t0;
  } else {
    return null;
  }
};
const ConfirmDetailsPopupModal = t0 => {
  const $ = _c(51);
  const {
    accountDetails,
    formOptions
  } = t0;
  const [status, setStatus] = useState(Status.SHOW_FORM);
  const confirmDetails = useDialog();
  const [completedMessage, setCompletedMessage] = useState("");
  const {
    t
  } = useTranslation("common");
  let t1;
  if ($[0] !== confirmDetails) {
    t1 = () => {
      let timeoutId;
      const tryOpen = () => {
        if (confirmDetails.dialogRef.current) {
          confirmDetails.open();
        } else {
          timeoutId = setTimeout(tryOpen, 100);
        }
      };
      tryOpen();
      return () => clearTimeout(timeoutId);
    };
    $[0] = confirmDetails;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [];
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  useEffect(t1, t2);
  let t3;
  if ($[3] !== status) {
    t3 = () => {
      if (status !== Status.SHOW_FORM) {
        return false;
      }
      setStatus(Status.SUBMITTING);
    };
    $[3] = status;
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  const onSubmitStart = t3;
  let t4;
  if ($[5] !== t) {
    t4 = response => {
      const immediateUpdate = !response?.adskTicketId;
      setCompletedMessage(t(immediateUpdate ? "Forms.AccountForm.success_immediate" : "Forms.AccountForm.success_pending"));
      setStatus(Status.SUCCESS);
    };
    $[5] = t;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const onSubmitEnd = t4;
  let t5;
  if ($[7] === Symbol.for("react.memo_cache_sentinel")) {
    t5 = () => {
      clientStorage.set(POPUP_STORAGE_KEY, true, 604800);
      setStatus(Status.FAILURE);
    };
    $[7] = t5;
  } else {
    t5 = $[7];
  }
  const onSubmitError = t5;
  let title;
  let modalBody;
  if (status === Status.SUCCESS) {
    let t6;
    if ($[8] !== t) {
      t6 = t("Forms.ConfirmDetails.thanks");
      $[8] = t;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    title = t6;
    let t7;
    if ($[10] === Symbol.for("react.memo_cache_sentinel")) {
      t7 = <Trans i18nKey="common:Forms.ConfirmDetails.success_message" components={{
        link: <a href="/account" />
      }} />;
      $[10] = t7;
    } else {
      t7 = $[10];
    }
    let t8;
    if ($[11] !== completedMessage) {
      t8 = <p className="success">{completedMessage}{" "}{t7}</p>;
      $[11] = completedMessage;
      $[12] = t8;
    } else {
      t8 = $[12];
    }
    modalBody = t8;
  } else {
    if (status === Status.FAILURE) {
      let t6;
      if ($[13] !== t) {
        t6 = t("Feedback.personalization_unavailable_error");
        $[13] = t;
        $[14] = t6;
      } else {
        t6 = $[14];
      }
      title = t6;
      let t7;
      if ($[15] !== t) {
        t7 = t("Forms.ConfirmDetails.unable_to_confirm");
        $[15] = t;
        $[16] = t7;
      } else {
        t7 = $[16];
      }
      let t8;
      if ($[17] !== t7) {
        t8 = <p className="error">{t7}</p>;
        $[17] = t7;
        $[18] = t8;
      } else {
        t8 = $[18];
      }
      let t9;
      if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
        t9 = <ContactDetails />;
        $[19] = t9;
      } else {
        t9 = $[19];
      }
      let t10;
      if ($[20] !== t8) {
        t10 = <>{t8}{t9}</>;
        $[20] = t8;
        $[21] = t10;
      } else {
        t10 = $[21];
      }
      modalBody = t10;
    } else {
      let t6;
      if ($[22] !== t) {
        t6 = t("Forms.ConfirmDetails.please_review");
        $[22] = t;
        $[23] = t6;
      } else {
        t6 = $[23];
      }
      title = t6;
      let t7;
      if ($[24] === Symbol.for("react.memo_cache_sentinel")) {
        t7 = <p><Trans i18nKey="common:Forms.ConfirmDetails.review_policy" components={{
            link: <a href="/privacy-policy" target="_blank" rel="noopener noreferrer" />
          }} /></p>;
        $[24] = t7;
      } else {
        t7 = $[24];
      }
      let t8;
      if ($[25] !== accountDetails || $[26] !== formOptions) {
        t8 = <AccountPreferencesForm accountDetails={accountDetails} formOptions={formOptions} />;
        $[25] = accountDetails;
        $[26] = formOptions;
        $[27] = t8;
      } else {
        t8 = $[27];
      }
      let t9;
      if ($[28] !== t) {
        t9 = t("Actions.confirm");
        $[28] = t;
        $[29] = t9;
      } else {
        t9 = $[29];
      }
      let t10;
      if ($[30] !== t9) {
        t10 = <button className="cds-button cds-button--primary" type="submit">{t9}</button>;
        $[30] = t9;
        $[31] = t10;
      } else {
        t10 = $[31];
      }
      let t11;
      if ($[32] !== t10 || $[33] !== t8) {
        t11 = <ModalForm>{t8}{t10}</ModalForm>;
        $[32] = t10;
        $[33] = t8;
        $[34] = t11;
      } else {
        t11 = $[34];
      }
      let t12;
      if ($[35] !== t) {
        t12 = t("Forms.ConfirmDetails.please_contact_us");
        $[35] = t;
        $[36] = t12;
      } else {
        t12 = $[36];
      }
      let t13;
      if ($[37] !== t12) {
        t13 = <p data-target="details-contact-notice">{t12}</p>;
        $[37] = t12;
        $[38] = t13;
      } else {
        t13 = $[38];
      }
      let t14;
      if ($[39] === Symbol.for("react.memo_cache_sentinel")) {
        t14 = <ContactDetails />;
        $[39] = t14;
      } else {
        t14 = $[39];
      }
      let t15;
      if ($[40] !== onSubmitEnd || $[41] !== onSubmitStart || $[42] !== t11 || $[43] !== t13) {
        t15 = <>{t7}<CsrfForm apiUrl="/users/account/confirm_details" autoComplete="off" onSubmitStart={onSubmitStart} onSubmitEnd={onSubmitEnd} onSubmitError={onSubmitError}>{t11}{t13}{t14}</CsrfForm></>;
        $[40] = onSubmitEnd;
        $[41] = onSubmitStart;
        $[42] = t11;
        $[43] = t13;
        $[44] = t15;
      } else {
        t15 = $[44];
      }
      modalBody = t15;
    }
  }
  const showCloseButton = status === Status.SUCCESS || status === Status.FAILURE;
  const t6 = showCloseButton ? _temp : null;
  let t7;
  if ($[45] !== confirmDetails || $[46] !== modalBody || $[47] !== showCloseButton || $[48] !== t6 || $[49] !== title) {
    t7 = <Dialog allowCancel={showCloseButton} {...confirmDetails} title={title} onClose={t6}>{modalBody}</Dialog>;
    $[45] = confirmDetails;
    $[46] = modalBody;
    $[47] = showCloseButton;
    $[48] = t6;
    $[49] = title;
    $[50] = t7;
  } else {
    t7 = $[50];
  }
  return t7;
};
interface IConfirmDetailsPopupModalProps {
  accountDetails: IAccountDetails;
  formOptions: ISignupOptions;
}
enum Status {
  SHOW_FORM = 'SHOW_FORM',
  SUBMITTING = 'SUBMITTING',
  SUCCESS = 'SUCCESS',
  FAILURE = 'FAILURE',
}
function _temp() {
  return window.location.reload();
}